import { createSelector } from 'reselect';

import {
  White,
  Black,
  BorderGrey,
  BrandBlue,
  BrandBlueOpaque,
  Red,
  Yellow,
  gray6,
  gray4
} from '../constants/Colors';

import FormStylesOptions from '../constants/FormStylesOptions';
import FieldPresentationModes from '../constants/FieldPresentationModes';
import FieldTypes from '../constants/FieldTypes';
import FieldModes from '../constants/FieldModes';

import { hexToRGBA } from '../lib/colors';

const internal = {

  primaryColor: BrandBlue,
  primaryOpaqueColor: BrandBlueOpaque,
  secondaryColor: gray4,
  dangerColor: Red,
  warningColor: Yellow,
  backgroundColor: White,

  padding: 5,
  margin: 1,

  fontFamily: 'sans-serif',
  fontSize: 12,

  fontColorPrimary: Black,
  fontColorSecondary: gray6,

  borderRadius: 5,
  borderWidth: 1,
  borderColor: BorderGrey,

};

/**
 * Get formatted theme object
 *
 * All theme properties that support numbers like fontSize, borderRadius, etc.
 * must be passed in as numbers. Number values default to px sizing.
 *
 * @param {Object} theme //Theme object passed in via developer
 * @returns {Object}
 */
export const getThemeObject = (theme = { page: {}, field: {} }) => {

  /**
   * >>>>>>>>>> Start Field Theme Properties <<<<<<<<<<
   */
  let columnTitleFontSize = internal.fontSize;
  if (typeof theme?.field?.columnTitleFontSize === 'number') columnTitleFontSize = theme?.field?.columnTitleFontSize;
  else if (typeof theme?.field?.fontSize === 'number') columnTitleFontSize = theme?.field?.fontSize;

  let columnTitlePadding = internal.padding;
  if (typeof theme?.field?.columnTitlePadding === 'number') columnTitlePadding = theme?.field?.columnTitlePadding;
  else if (typeof theme?.field?.padding === 'number') columnTitlePadding = theme?.field?.padding;

  let borderRadius = internal.borderRadius;
  if (typeof theme?.field?.borderRadius === 'number') borderRadius = theme?.field?.borderRadius;
  else if (typeof theme?.borderRadius === 'number') borderRadius = theme?.borderRadius;

  let borderWidth = internal.borderWidth;
  if (typeof theme?.field?.borderWidth === 'number') borderWidth = theme?.field?.borderWidth;
  else if (typeof theme?.borderWidth === 'number') borderWidth = theme?.borderWidth;

  /**
   * >>>>>>>>>> End Field Theme Properties <<<<<<<<<<
   */

  return {

    primaryColor: theme?.primaryColor || internal.primaryColor,
    primaryOpaqueColor: theme?.primaryOpaqueColor || internal.primaryOpaqueColor || internal.primaryColor,
    secondaryColor: theme?.secondaryColor || internal.secondaryColor,
    dangerColor: theme?.dangerColor || internal.dangerColor,
    warningColor: theme?.warningColor || internal.warningColor,
    backgroundColor: theme?.backgroundColor || internal.backgroundColor,

    borderColor: theme?.borderColor || internal.borderColor,
    borderRadius: typeof theme?.borderRadius === 'number' ? theme?.borderRadius : internal.borderRadius,
    borderWidth: typeof theme?.borderWidth === 'number' ? theme?.borderWidth : internal.borderWidth,

    fontFamily: theme?.fontFamily || internal.fontFamily,
    fontColorPrimary: theme?.fontColorPrimary || internal.fontColorPrimary,
    fontColorSecondary: theme?.fontColorSecondary || internal.fontColorSecondary,

    page: {

      backgroundColor: theme?.page?.backgroundColor || theme?.backgroundColor || internal.backgroundColor,
      padding: theme?.page?.padding || 24,

      borderColor: theme?.page?.borderColor || theme?.borderColor || internal.borderColor,
      borderRadius: theme?.page?.borderRadius || theme?.borderRadius || internal.borderRadius,
      borderWidth: theme?.page?.borderWidth || theme?.borderWidth || internal.borderWidth,

    },
    field: {

      titleFontSize: typeof theme?.field?.titleFontSize === 'number' ? theme?.field?.titleFontSize : internal.fontSize,
      titleFontColor: theme?.field?.titleFontColor || theme?.fontColorPrimary || internal.fontColorPrimary,
      titleFontStyle: theme?.field?.titleFontStyle || FormStylesOptions.fontStyleNormal,
      titleFontWeight: theme?.field?.titleFontWeight || FormStylesOptions.fontWeightBold,
      titleTextAlign: theme?.field?.titleTextAlign || FormStylesOptions.textAlignLeft,
      titleTextTransform: theme?.field?.titleTextTransform || FormStylesOptions.textTransformNone,
      titleTextDecoration: theme?.field?.titleTextDecoration || FormStylesOptions.textDecorationNone,

      fontSize: typeof theme?.field?.fontSize === 'number' ? theme?.field?.fontSize : internal.fontSize,
      fontColor: theme?.field?.fontColor || theme?.fontColorPrimary || internal.fontColorPrimary,
      fontStyle: theme?.field?.fontStyle || FormStylesOptions.fontStyleNormal,
      fontWeight: theme?.field?.fontWeight || FormStylesOptions.fontWeightNormal,
      textAlign: theme?.field?.textAlign || FormStylesOptions.textAlignLeft,
      textTransform: theme?.field?.textTransform || FormStylesOptions.textTransformNone,
      textDecoration: theme?.field?.textDecoration || FormStylesOptions.textDecorationNone,
      textOverflow: theme?.field?.textOverflow || FormStylesOptions.textOverflowNone,
      padding: typeof theme?.field?.padding === 'number' ? theme?.field?.padding : internal.padding,
      margin: typeof theme?.field?.margin === 'number' ? theme?.field?.margin : internal.margin,
      borderRadius,
      borderWidth,
      borderColor: theme?.field?.borderColor || theme?.borderColor || internal.borderColor,

      /**
       * IMPORTANT NOTE: Transparent backgrounds have been the previously supported background color
       * for many existing forms.
       *
       * Forms will be negatively impacted if changed to anything besides 'transparent', especially for
       * PDF Forms that were built and don't properly have the field presentation set to "transparent".
       */
      backgroundColor: theme?.field?.backgroundColor || theme?.backgroundColor || 'transparent',

      columnTitleFontSize,
      columnTitlePadding,
      columnTitleFontColor: theme?.field?.columnTitleFontColor || theme?.field?.fontColor || theme?.fontColorPrimary || internal.fontColorPrimary,
      columnTitleTextAlign: theme?.field?.columnTitleTextAlign || theme?.field?.textAlign || FormStylesOptions.textAlignLeft,
      columnTitleFontStyle: theme?.field?.columnTitleFontStyle || theme?.field?.fontStyle || FormStylesOptions.fontStyleNormal,
      columnTitleFontWeight: theme?.field?.columnTitleFontWeight || FormStylesOptions.fontWeightBold,
      columnTitleTextTransform: theme?.field?.columnTitleTextTransform || theme?.field?.textTransform || FormStylesOptions.textTransformNone,
      columnTitleTextDecoration: theme?.field?.columnTitleTextDecoration || theme?.field?.textDecoration || FormStylesOptions.textDecorationNone,
      columnTitleBackgroundColor: theme?.field?.columnTitleBackgroundColor || theme?.secondaryColor || internal.secondaryColor

    }
  };

};

/**
 * Apply the proper styles to a field object
 *
 * Order of applied styles for a field
 * 1. Field default styles
 * 2. Field specific default styles
 * 3. Field page presentation (transparent) and readonly mode styles
 * 4. File Styles
 * 5. Theme Styles
 *
 * @param {Object} fieldStyles //Styles set on the fieldPosition object
 * @param {Object} fileStyles //Styles set on the file
 * @param {Object} theme //Styles set on the theme.field property theme object generated by getThemeObject helper method.
 * @param {Object} fieldPresentationMode //Transparent or normal
 * @returns {Object}
 */
/**
 * Resolve a number that may be set on the fieldPosition, then on the file styles, and may
 * legitimately be unset.
 *
 * A typeof check is used rather than || so a deliberate 0 is not treated as unset. Undefined
 * is returned when neither is set, so the consumer owns the default.
 *
 * @param {Number} fieldPositionValue
 * @param {Number} fileStylesValue
 * @returns {Number|undefined}
 */
const resolveOptionalNumber = (fieldPositionValue, fileStylesValue) => {

  if (typeof fieldPositionValue === 'number') return fieldPositionValue;

  if (typeof fileStylesValue === 'number') return fileStylesValue;

  return undefined;

};

/**
 * Resolve a style value down the fieldPosition -> fileStyles -> theme cascade in cases
 * where an empty string is a real setting rather than an absent one.
 *
 * textOverflow is the reason this exists. FormStylesOptions.textOverflowNone is '',
 * meaning "wrap, do not ellipsize", and it is what the settings panel writes when the
 * wrap button is pressed. Because '' is falsy, a || cascade cannot tell a deliberate
 * wrap apart from a value that was never supplied, so it discarded the wrap and
 * inherited the file level 'ellipsis' instead.
 *
 * Presence decides here, not truthiness. A level with no opinion leaves the key
 * undefined, and only then do we look at the level below it.
 *
 * @param {...String} values Cascade values, highest precedence first
 * @returns {String|undefined}
 */
const resolveOptionalString = (...values) => values.find((value) => typeof value === 'string');

export const getFieldThemeObject = createSelector(
  (state) => state.mode,
  (state) => state.fieldPresentationMode,
  (state) => state.fieldPosition,
  (state) => state.fileStyles,
  (state) => state.theme,
  (state) => state.sdkTheme,
  (mode, fieldPresentationMode, fieldPosition = {}, fileStyles = {}, theme = {}, sdkTheme = {}) => {

    const isTransparent = fieldPresentationMode === FieldPresentationModes.transparent;
    const isReadonly = mode === FieldModes.readonly || mode === FieldModes.pdf;

    /**
     * Step 1: Set defaults
     */
    let titleDisplay = fieldPosition?.titleDisplay || FormStylesOptions.displayInline;
    let columnTitleFontSize = theme?.columnTitleFontSize;
    let columnTitlePadding = theme?.columnTitlePadding;
    let titleFontSize = theme?.titleFontSize;
    let fontSize = theme?.fontSize;
    let padding = theme?.padding;
    let margin = theme?.margin;
    let borderRadius = theme?.borderRadius;
    let borderWidth = theme?.borderWidth;
    let borderColor = theme?.borderColor;
    let backgroundColor = theme?.backgroundColor;
    let columnTitleBackgroundColor = theme?.columnTitleBackgroundColor;

    /**
     * Step 2: Handle special field types
     */
    if (fieldPosition.type === FieldTypes.block || fieldPosition.type === FieldTypes.richText) {

      padding = 0;
      margin = 0;
      borderColor = 'transparent';
      backgroundColor = 'transparent';

    }

    /**
     * Step 3: Handle page field transparent mode and readonly sdk mode
     */
    if (isTransparent) {

      titleDisplay = fieldPosition?.titleDisplay || FormStylesOptions.displayNone;
      padding = 0;
      margin = 0;
      borderWidth = 1;
      borderRadius = 0;

      if (isReadonly) {
        columnTitleBackgroundColor = 'transparent';
        backgroundColor = 'transparent';
        borderColor = 'transparent';
      } else {
        columnTitleBackgroundColor = hexToRGBA(sdkTheme.primaryColor, '0.1');
        backgroundColor = hexToRGBA(sdkTheme.primaryColor, '0.1');
        borderColor = hexToRGBA(sdkTheme.primaryColor, '0.3');
      }

    }

    /**
     * Step 4: Apply saved/set theme styles on field first and then file styles secondary
     */
    if (typeof fieldPosition?.columnTitleFontSize === 'number') columnTitleFontSize = fieldPosition?.columnTitleFontSize;
    else if (typeof fileStyles?.fontSize === 'number') columnTitleFontSize = fileStyles.columnTitleFontSize;

    if (typeof fieldPosition?.columnTitlePadding === 'number') columnTitlePadding = fieldPosition?.columnTitlePadding;
    else if (typeof fileStyles?.padding === 'number') columnTitlePadding = fileStyles?.padding;

    if (typeof fieldPosition?.margin === 'number' && !isTransparent) margin = fieldPosition?.margin;
    else if (typeof fileStyles?.margin === 'number' && !isTransparent) margin = fileStyles.margin;

    if (typeof fieldPosition?.titleFontSize === 'number') titleFontSize = fieldPosition?.titleFontSize;
    else if (typeof fileStyles?.titleFontSize === 'number') titleFontSize = fileStyles.titleFontSize;

    if (typeof fieldPosition?.fontSize === 'number') fontSize = fieldPosition?.fontSize;
    else if (typeof fileStyles?.fontSize === 'number') fontSize = fileStyles.fontSize;

    /**
     * IMPORTANT NOTE: PADDING
     *
     * Why do we check if fieldPosition.padding is a number and not 0 (zero). This is a
     * requirement for pre-existing forms. Pre-existing forms inherit padding from file.styles
     * if fieldPosition.padding is undefined or 0. Removing this requirement will negatively
     * impact field layouts and data alignment, especially on pre-existing PDF Forms.
     */
    if (typeof fieldPosition?.padding === 'number' && fieldPosition?.padding) padding = fieldPosition?.padding;
    else if (typeof fileStyles?.padding === 'number') padding = fileStyles.padding;

    if (typeof fieldPosition?.borderRadius === 'number') borderRadius = fieldPosition?.borderRadius;
    else if (typeof fileStyles?.borderRadius === 'number') borderRadius = fileStyles.borderRadius;

    if (typeof fieldPosition?.borderWidth === 'number') borderWidth = fieldPosition?.borderWidth;
    else if (typeof fileStyles?.borderWidth === 'number') borderWidth = fileStyles.borderWidth;

    if (fieldPosition?.borderColor) borderColor = fieldPosition?.borderColor;
    else if (fileStyles?.borderColor) borderColor = fileStyles.borderColor;

    if (fieldPosition?.backgroundColor) backgroundColor = fieldPosition?.backgroundColor;
    else if (fileStyles?.backgroundColor) backgroundColor = fileStyles.backgroundColor;

    if (fieldPosition?.columnTitleBackgroundColor) columnTitleBackgroundColor = fieldPosition?.columnTitleBackgroundColor;

    const fieldTheme = {

      //Theme
      primaryColor: sdkTheme?.primaryColor,
      secondaryColor: sdkTheme?.secondaryColor,
      dangerColor: sdkTheme?.dangerColor,
      warningColor: sdkTheme?.warningColor,
      fontFamily: sdkTheme?.fontFamily,

      //Title Theme
      titleDisplay,
      titleFontSize,
      titleFontColor: fieldPosition?.titleFontColor || fileStyles?.titleFontColor || theme?.titleFontColor,
      titleFontStyle: fieldPosition?.titleFontStyle || fileStyles?.titleFontStyle || theme?.titleFontStyle,
      titleFontWeight: fieldPosition?.titleFontWeight || fileStyles?.titleFontWeight || theme?.titleFontWeight,
      titleTextAlign: fieldPosition?.titleTextAlign || fileStyles?.titleTextAlign || theme?.titleTextAlign,
      titleTextTransform: fieldPosition?.titleTextTransform || fileStyles?.titleTextTransform || theme?.titleTextTransform,
      titleTextDecoration: fieldPosition?.titleTextDecoration || fileStyles?.titleTextDecoration || theme?.titleTextDecoration,

      //Field Theme
      padding,
      margin,
      fontSize,
      fontColor: fieldPosition?.fontColor || fileStyles?.fontColor || theme?.fontColor,
      fontStyle: fieldPosition?.fontStyle || fileStyles?.fontStyle || theme?.fontStyle,
      fontWeight: fieldPosition?.fontWeight || fileStyles?.fontWeight || theme?.fontWeight,
      textAlign: fieldPosition?.textAlign || fileStyles?.textAlign || theme?.textAlign,
      textTransform: fieldPosition?.textTransform || fileStyles?.textTransform || theme?.textTransform,
      textDecoration: fieldPosition?.textDecoration || fileStyles?.textDecoration || theme?.textDecoration,
      /**
       * textOverflow is resolved by presence rather than truthiness, because its "wrap"
       * value is the empty string. See resolveOptionalString above.
       */
      textOverflow: resolveOptionalString(
        fieldPosition?.textOverflow,
        fileStyles?.textOverflow,
        theme?.textOverflow
      ),

      /**
       * <----------------------------NOTICE------------------------->
       * Auto-shrink text properties.
       *
       * These must resolve to flat, primitive values. The exporter runs this selector twice
       * over the same data: getPageFieldPositionsForPDFRenerer merges the result back onto
       * the fieldPosition, and that merged object is then handed to Field which calls this
       * selector again. Primitives survive that round trip unchanged. A nested object would
       * not, and auto-shrink would silently switch itself off in PDF exports only.
       *
       * Flat values also keep the shallow prop comparison in TextField and TextareaField
       * (both PureComponents) working, since every field type receives these keys.
       *
       * The boolean checks fieldPosition first and uses it whenever it is an actual boolean,
       * including false, so a single field can opt out of a file wide default.
       */
      autoShrink: typeof fieldPosition?.autoShrink === 'boolean'
        ? fieldPosition.autoShrink
        : fileStyles?.autoShrink === true,

      /**
       * Numbers use a typeof check rather than || so a deliberate 0 is not treated as unset.
       * Undefined is left in place on purpose, so autoShrinkHelper stays the single place
       * that owns the defaults of 2 and 100.
       */
      maxLines: resolveOptionalNumber(fieldPosition?.maxLines, fileStyles?.maxLines),

      maxLength: resolveOptionalNumber(fieldPosition?.maxLength, fileStyles?.maxLength),
      borderRadius,
      borderWidth,
      borderColor,
      backgroundColor,

      //Table Column Theme
      columnTitleFontSize,
      columnTitlePadding,
      columnTitleFontColor: fieldPosition?.columnTitleFontColor || fileStyles?.fontColor || theme?.columnTitleFontColor,
      columnTitleTextAlign: fieldPosition?.columnTitleTextAlign || fileStyles?.textAlign || theme?.columnTitleTextAlign,
      columnTitleFontStyle: fieldPosition?.columnTitleFontStyle || fileStyles?.fontStyle || theme?.fontStyle,
      columnTitleFontWeight: fieldPosition?.columnTitleFontWeight || theme?.columnTitleFontWeight,
      columnTitleTextTransform: fieldPosition?.columnTitleTextTransform || fileStyles?.textTransform || theme?.columnTitleTextTransform,
      columnTitleTextDecoration: fieldPosition?.columnTitleTextDecoration || fileStyles?.textDecoration || theme?.columnTitleTextDecoration,
      columnTitleBackgroundColor

    };

    return fieldTheme;

  }
);

export default getThemeObject;
